import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { savePqrsdConfig } from "../../../services/backend/PqrsdConfigService";
import {
  DEFAULT_CITIZEN_SURVEY,
  MAX_BUTTON_LABEL,
  MAX_LABEL,
  MAX_OPTIONS,
  MAX_REASONS,
  OTHER_REASON_LABEL,
  normalizeCitizenSurvey,
  validateCitizenSurvey,
} from "../../../services/backend/gfunctions/pqrsd/_shared/citizenSurvey";

import { pqrsdText } from "../pqrsdLabel";
const SCORES = [5, 4, 3, 2, 1];
const clone = (v) => JSON.parse(JSON.stringify(v));

// Moves item i of a list one place up (-1) or down (+1).
const move = (list, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = list.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// Config → PQRSD → Encuesta: the satisfaction survey the citizen answers on
// /pqrsd/estado after a final answer. Saved on its own (config/pqrsd.citizenSurvey),
// never by the General tab's "Guardar ajustes". Open to any PQRSD.CONFIG operator.
export default function PqrsdSurveyConfig({ survey, onSurveyChange, onFeedback }) {
  const stored = useMemo(() => normalizeCitizenSurvey(survey), [survey]);
  const [draft, setDraft] = useState(() => clone(stored));
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => { setDraft(clone(stored)); setTouched(false); }, [stored]);

  const errors = validateCitizenSurvey(draft);
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);

  const edit = (fn) => { setDraft((d) => { const next = clone(d); fn(next); return next; }); setTouched(true); };
  const setOption = (i, key, value) => edit((d) => { d.options[i][key] = value; });
  const setReason = (i, value) => edit((d) => { d.reopen.reasons[i].label = value; });

  const save = async () => {
    setTouched(true);
    if (errors.length) return;
    setSaving(true);
    // Normalized before storing: trimmed labels, stable ids for new rows.
    const citizenSurvey = normalizeCitizenSurvey(draft);
    const ok = await savePqrsdConfig({ citizenSurvey });
    setSaving(false);
    if (!ok) {
      onFeedback({ severity: "error", msg: "No fue posible guardar la encuesta." });
      return;
    }
    onSurveyChange(citizenSurvey);
    onFeedback({ severity: "success", msg: "Encuesta guardada. Los ciudadanos verán los cambios de inmediato." });
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {pqrsdText("Cuando la PQRSD recibe su respuesta final, el ciudadano la califica en la página de consulta. Elegir una calificación ")}<strong>cierra</strong>{pqrsdText(" la PQRSD; la opción para reabrir exige un motivo y una explicación, y la devuelve a trámite con un plazo nuevo.")}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <TextField
          fullWidth
          label="Pregunta"
          value={draft.question}
          inputProps={{ maxLength: 200 }}
          onChange={(e) => edit((d) => { d.question = e.target.value; })}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Opciones de calificación</Typography>
          <Chip size="small" label={`${draft.options.length} / ${MAX_OPTIONS}`} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          El puntaje (1 a 5) no lo ve el ciudadano: alimenta el promedio de satisfacción de los informes.
        </Typography>
        <Stack spacing={1.5}>
          {draft.options.map((o, i) => (
            <Stack key={o.id || `new-${i}`} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <TextField
                size="small"
                fullWidth
                label={`Opción ${i + 1}`}
                value={o.label}
                inputProps={{ maxLength: MAX_LABEL }}
                onChange={(e) => setOption(i, "label", e.target.value)}
              />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Puntaje"
                  value={o.score}
                  onChange={(e) => setOption(i, "score", Number(e.target.value))}
                  sx={{ width: 100 }}
                >
                  {SCORES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
                <IconButton size="small" aria-label="Subir" disabled={i === 0}
                  onClick={() => edit((d) => { d.options = move(d.options, i, -1); })}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Bajar" disabled={i === draft.options.length - 1}
                  onClick={() => edit((d) => { d.options = move(d.options, i, 1); })}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <Tooltip title={draft.options.length === 1 ? "Debe quedar al menos una opción" : "Eliminar"}>
                  <span>
                    <IconButton size="small" aria-label="Eliminar opción" disabled={draft.options.length === 1}
                      onClick={() => edit((d) => { d.options.splice(i, 1); })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          ))}
        </Stack>
        <Button
          sx={{ mt: 1.5 }}
          startIcon={<AddIcon />}
          disabled={draft.options.length >= MAX_OPTIONS}
          onClick={() => edit((d) => { d.options.push({ id: "", label: "", score: 3 }); })}
        >
          Agregar opción
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderColor: "warning.main" }}>
        <Typography variant="subtitle1">Opción para reabrir (obligatoria)</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Siempre aparece de última. Puede cambiar su texto, no quitarla.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Texto de la opción"
          value={draft.reopen.label}
          inputProps={{ maxLength: MAX_LABEL }}
          onChange={(e) => edit((d) => { d.reopen.label = e.target.value; })}
        />
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 2, maxWidth: 360 }}
          label="Texto del botón que envía la reapertura"
          value={draft.reopen.buttonLabel ?? ""}
          inputProps={{ maxLength: MAX_BUTTON_LABEL }}
          helperText="Aparece cuando el ciudadano elige reabrir. Ej.: «Reabrir la solicitud»."
          onChange={(e) => edit((d) => { d.reopen.buttonLabel = e.target.value; })}
        />
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Motivos para reabrir</Typography>
          <Chip size="small" label={`${draft.reopen.reasons.length} / ${MAX_REASONS}`} />
        </Stack>
        <Stack spacing={1.5}>
          {draft.reopen.reasons.map((r, i) => (
            <Stack key={r.id || `new-${i}`} direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                fullWidth
                label={`Motivo ${i + 1}`}
                value={r.label}
                inputProps={{ maxLength: MAX_LABEL }}
                onChange={(e) => setReason(i, e.target.value)}
              />
              <IconButton size="small" aria-label="Eliminar motivo"
                onClick={() => edit((d) => { d.reopen.reasons.splice(i, 1); })}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <TextField size="small" fullWidth disabled label="Siempre disponible" value={`${OTHER_REASON_LABEL} (fijo)`} />
        </Stack>
        <Button
          sx={{ mt: 1.5 }}
          startIcon={<AddIcon />}
          disabled={draft.reopen.reasons.length >= MAX_REASONS}
          onClick={() => edit((d) => { d.reopen.reasons.push({ id: "", label: "" }); })}
        >
          Agregar motivo
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Con cualquier motivo, el ciudadano debe explicar con detalle qué necesita (mínimo 10 caracteres).
        </Typography>
      </Paper>

      {touched && errors.length > 0 && (
        <Alert severity="error">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={() => { setDraft(clone(DEFAULT_CITIZEN_SURVEY)); setTouched(true); }}
          disabled={saving}
        >
          Restaurar predeterminada
        </Button>
        <Button onClick={() => { setDraft(clone(stored)); setTouched(false); }} disabled={saving || !dirty}>
          Descartar cambios
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !dirty}>
          {saving ? "Guardando…" : "Guardar encuesta"}
        </Button>
      </Box>
    </Stack>
  );
}
